import React from 'react';
import {
  Alert, Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid,
  MenuItem, TextField, Typography,
} from '@mui/material';
import { PROVINCES, citiesFor } from '../geography.js';

export default function ServiceAreaStep({ answers, errors, onChange }) {
  const sa = answers.serviceArea;
  const set = patch => onChange({ ...answers, serviceArea: { ...sa, ...patch } });
  const localOptions = citiesFor(sa.homeProvince).filter(city => city !== sa.homeCity);

  const toggleLocal = city => set({
    localCities: sa.localCities.includes(city)
      ? sa.localCities.filter(c => c !== city)
      : [...sa.localCities, city],
  });
  const toggleDestination = code => set({
    destinationProvinces: sa.destinationProvinces.includes(code)
      ? sa.destinationProvinces.filter(p => p !== code)
      : [...sa.destinationProvinces, code],
  });

  return (
    <>
      <Typography variant="h5" fontWeight={600} gutterBottom>Where do you work?</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your service area determines which customer moves MOVD can show you.
      </Typography>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>HOME BASE</Typography>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth select required label="Province" value={sa.homeProvince}
            error={!!errors.homeProvince} helperText={errors.homeProvince}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 240, '& .MuiSelect-select': { minWidth: 200 } }}
            onChange={e => set({ homeProvince: e.target.value, homeCity: '', localCities: [] })}>
            {PROVINCES.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth select required label="City" value={sa.homeCity} disabled={!sa.homeProvince}
            error={!!errors.homeCity} helperText={errors.homeCity}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 240, '& .MuiSelect-select': { minWidth: 200 } }}
            onChange={e => set({ homeCity: e.target.value })}>
            {citiesFor(sa.homeProvince).map(city => <MenuItem key={city} value={city}>{city}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      {sa.homeProvince && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>LOCAL SERVICE AREA</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your home city ({sa.homeCity || '—'}) is always included. Add any other communities you serve locally.
          </Typography>
          <FormGroup row>
            {localOptions.map(city => (
              <FormControlLabel
                key={city}
                control={<Checkbox checked={sa.localCities.includes(city)} onChange={() => toggleLocal(city)} />}
                label={city}
                sx={{ width: { xs: '50%', sm: '33%' } }}
              />
            ))}
          </FormGroup>
        </>
      )}

      {answers.services.longDistance && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>LONG-DISTANCE SERVICE</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Besides {sa.homeProvince ? PROVINCES.find(p => p.code === sa.homeProvince)?.name : 'your home province'}, which provinces do you move customers to?
          </Typography>
          {errors.destinationProvinces && <Alert severity="error" sx={{ mb: 1.5 }}>{errors.destinationProvinces}</Alert>}
          <FormGroup row>
            {PROVINCES.filter(p => p.code !== sa.homeProvince).map(p => (
              <FormControlLabel
                key={p.code}
                control={<Checkbox checked={sa.destinationProvinces.includes(p.code)} onChange={() => toggleDestination(p.code)} />}
                label={p.name}
                sx={{ width: { xs: '50%', sm: '33%' } }}
              />
            ))}
          </FormGroup>
        </>
      )}

      {!answers.services.longDistance && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            Long-distance moving is not enabled, so we'll only match you with moves inside your local service area.
          </Alert>
        </Box>
      )}
    </>
  );
}
